/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { AglynHost, ScreenUid } from '@aglyn/aglyn'
import { TENANT_APEX } from '@aglyn/aglyn/app-utils/host-naming'
import {
  isLocalConsole,
  isPreviewConsole,
  siteLiveUrlForSlug,
} from '@aglyn/aglyn/app-utils/site-live-url'
import type { CollectionTemplateRoute } from './collection-templates'

// The origin rules live beside the URL builder in `libs/aglyn` (AGL-3758),
// so a plugin's console card opens the page these links do.
export { isLocalConsole, isPreviewConsole }

/**
 * The apex that assigned site subdomains hang off — `{subdomain}.aglyn.app` on
 * Aglyn's cloud, the operator's own apex on a self-host install (AGL-2022).
 *
 * This was a bare `'aglyn.app'` literal while `NEXT_PUBLIC_TENANT_DOMAIN` —
 * the same value, same default — was already read by
 * `app/api/screens/revalidate/route.ts` and `utils/server/org-lockdown.ts`.
 * So a self-hoster who set it got a console that revalidated and locked down
 * their apex while every "View live" link and every site address it displayed
 * still named `aglyn.app`: our infrastructure, printed to their users, for
 * sites we do not serve. Configured value first, our value only as the
 * default, so a self-host install is never pointed at our origin.
 *
 * Re-exported from `TENANT_APEX` rather than read again here (AGL-2195).
 * This module had its own `process.env.NEXT_PUBLIC_TENANT_DOMAIN ||
 * 'aglyn.app'`, byte-for-byte the third copy of the same expression, and a
 * fourth copy with the apex written in flat was still live in the staff host
 * page. One reader means an operator's apex cannot be honoured on some
 * surfaces and ignored on others.
 *
 * Dot notation is load-bearing and `TENANT_APEX` uses it: this module reaches
 * client components (`host-switcher-nav`), and Next inlines `NEXT_PUBLIC_*`
 * into the browser bundle by substituting `process.env.NAME` textually. The
 * bracket form the two server-side readers use is never substituted, and
 * would read `undefined` in the browser.
 */
const TENANT_PRODUCTION_ROOT = TENANT_APEX

/**
 * The site's public address for display (AGL-632): its custom domain when
 * connected, otherwise the assigned Aglyn subdomain (`{subdomain}.aglyn.app`).
 * Always the production domain — this is a label, not a live link.
 */
export function hostDisplayDomain(
  host: { cname?: string; subdomain?: string } | undefined,
): string | undefined {
  if (!host) return undefined
  return (
    host.cname ||
    (host.subdomain ? `${host.subdomain}.${TENANT_PRODUCTION_ROOT}` : undefined)
  )
}

/**
 * The site's address on the PLATFORM apex specifically, ignoring any custom
 * domain — `{subdomain}.{apex}` (AGL-2172).
 *
 * Distinct from {@link hostDisplayDomain}, which answers "what does this site
 * call itself" and prefers the custom domain. The Sites list shows both as
 * separate rows, so it needs the platform address even when a custom domain
 * exists, and it was re-deriving the `sub.aglyn.app` form by hand to get it —
 * printing our apex on a self-hoster's own console. Exported as a function
 * rather than exporting `TENANT_PRODUCTION_ROOT` so the apex stays owned by
 * this module and call sites cannot start concatenating it again.
 */
export function hostPlatformDomain(
  host: { subdomain?: string } | undefined,
): string | undefined {
  return host?.subdomain
    ? `${host.subdomain}.${TENANT_PRODUCTION_ROOT}`
    : undefined
}

export interface ResolvedScreenLiveUrl {
  url?: string
  /**
   * Why there is no live URL, when the author deserves an explanation
   * rather than a silently absent control (AGL-1271). Only set for
   * collection templates — an ordinary unpublished screen stays reason-less,
   * matching every other unpublished screen.
   */
  unavailableReason?: string
}

/**
 * The live URL a screen ACTUALLY renders at (AGL-1271).
 *
 * `buildScreenLiveUrl` reads the screen's own routing-map entry — which
 * AGL-1267 deliberately dropped for collection templates, so for a template
 * it either produces nothing or, worse, the dead `/blog-entry-template`
 * path the map briefly carried. What a template really renders is decided
 * by the collection that designates it (`collectCollectionTemplateRoutes`,
 * mirroring the tenant's `resolveCollectionTemplateScreenId`):
 *
 *   list template   → `/{collectionSlug}` — a real, single live page
 *   entry template  → `/{collectionSlug}/{entry}`, one page per entry —
 *                     no single URL exists, and the control should say so
 *   designated but slugless/superseded → nothing renders it; say why
 *   ordinary screen → `buildScreenLiveUrl`, unchanged
 */
export function resolveScreenLiveUrl(
  host: AglynHost | undefined,
  screenId: ScreenUid,
  template: {
    isTemplate: boolean
    routes: readonly CollectionTemplateRoute[] | undefined
  },
  consoleHostname?: string,
): ResolvedScreenLiveUrl {
  if (!template.isTemplate) {
    return { url: buildScreenLiveUrl(host, screenId, consoleHostname) }
  }
  const routes = template.routes ?? []
  // Sorted, not `find`: one screen can be the list template of two
  // collections, and `collectCollectionTemplateRoutes` emits them in the
  // order the collections listener happened to deliver. Picking the first
  // match would send "View live" to a different collection between renders.
  const listRoute = routes
    .filter((route) => route.role === 'list' && route.collectionSlug)
    .sort((a, b) => a.collectionSlug.localeCompare(b.collectionSlug))[0]
  if (listRoute) {
    // The list page renders this screen whether or not the template was
    // ever published at its own slug — the collection branch resolves the
    // screen through `listScreenId`, not the routing map.
    return {
      url: buildLiveUrlForSlug(
        host,
        `/${listRoute.collectionSlug}`,
        consoleHostname,
      ),
    }
  }
  const entryRoute = routes.find((route) => route.role === 'entry')
  if (entryRoute) {
    return {
      unavailableReason:
        `Renders /${entryRoute.collectionSlug}/{entry} — one page per ` +
        'published entry, so there is no single live address. Open an ' +
        `entry from the live /${entryRoute.collectionSlug} list instead.`,
    }
  }
  return {
    unavailableReason:
      'This template has no live address — the collection it serves has ' +
      'no slug, or another page has taken over as its template.',
  }
}

export function buildScreenLiveUrl(
  host: AglynHost | undefined,
  screenId: ScreenUid,
  /** Console hostname; defaults to the browser's. Injectable for tests. */
  consoleHostname: string | undefined = typeof window !== 'undefined'
    ? window.location.hostname
    : undefined,
): string | undefined {
  if (!host) return undefined
  const slug = host.screens?.[screenId]
  if (slug == null) return undefined
  return buildLiveUrlForSlug(host, String(slug), consoleHostname)
}

/**
 * The live URL of a site's home, on the same origin rules as a screen's: the
 * custom domain, else the platform subdomain, else — on a preview or local
 * console — the tenant preview origin with `?tenantHost=`.
 */
export function buildSiteLiveUrl(
  host: Pick<AglynHost, 'subdomain' | 'cname'> | undefined,
  consoleHostname: string | undefined = typeof window !== 'undefined'
    ? window.location.hostname
    : undefined,
): string | undefined {
  return buildLiveUrlForSlug(host as AglynHost | undefined, '/', consoleHostname)
}

/** The live URL for a site-relative slug on this host's real origin. */
function buildLiveUrlForSlug(
  host: AglynHost | undefined,
  slug: string,
  consoleHostname: string | undefined = typeof window !== 'undefined'
    ? window.location.hostname
    : undefined,
): string | undefined {
  return siteLiveUrlForSlug(host, slug, consoleHostname)
}
